import { HASH_INTERVAL, type IssuedCommand, readSnapshotInfo, TICKS_PER_SECOND } from '@am/sim';
import {
  type ClientMessage,
  MAX_CHAT_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PLAYERS,
  type PlayerInfo,
  type ServerMessage,
  SPEEDS,
} from './messages';

/** Anything that can deliver server messages to one player (a WebSocket, an in-memory loopback…). */
export interface RoomPeer {
  send(msg: ServerMessage): void;
  /** Drop the connection, e.g. after a sync timeout. */
  close(): void;
}

interface Member {
  readonly id: number;
  readonly name: string;
  readonly peer: RoomPeer;
  /** False until the player has loaded the world (new world or snapshot). */
  ready: boolean;
}

interface SyncState {
  /** The snapshot must describe the world right before this bundle. */
  readonly seq: number;
  readonly targets: Set<number>;
  readonly deadline: number;
}

/** Bundles issued in one update at most, so a stalled host can't flood clients. */
const MAX_BUNDLES_PER_UPDATE = 10;
const SYNC_TIMEOUT_MS = 20_000;
/** How many hash rounds to remember while waiting for the authority's hash. */
const HASH_HISTORY = 20;

/**
 * Lockstep room: the single clock of a co-op game. It orders player commands into numbered
 * bundles, runs no simulation, and coordinates joins and desync recovery with snapshots taken
 * from the *authority* (the longest-present ready player).
 *
 * Transport-agnostic and timer-free: the host calls `update(now)` regularly. The relay server and
 * the offline client both run this exact class, so single-player is literally one-player co-op.
 */
export class Room {
  private readonly members = new Map<number, Member>();
  private nextPlayerId = 1;
  private nextSeq = 0;
  private pending: IssuedCommand[] = [];
  private speed = 1;
  private lastUpdate: number | null = null;
  private owedTicks = 0;
  private sync: SyncState | null = null;
  private readonly hashes = new Map<number, Map<number, number>>();

  /** A saved game the room starts from instead of a new world; handed to the first player. */
  private initial: Uint8Array | null;

  constructor(
    readonly code: string,
    private readonly seed: number,
    initial: Uint8Array | null = null,
  ) {
    this.initial = initial;
    // Bundles continue from where the saved world left off.
    if (initial) this.nextSeq = readSnapshotInfo(initial).seq;
  }

  get isEmpty(): boolean {
    return this.members.size === 0;
  }

  get playerCount(): number {
    return this.members.size;
  }

  /** The longest-present ready player; its world is the reference during syncs and hash checks. */
  get authorityId(): number {
    for (const m of this.members.values()) if (m.ready) return m.id;
    return 0;
  }

  /** Adds a player and returns its id, or null if the room is full. */
  join(peer: RoomPeer, rawName: string, now: number): number | null {
    if (this.members.size >= MAX_PLAYERS) {
      peer.send({ type: 'error', code: 'roomFull', message: 'Room is full' });
      return null;
    }
    const id = this.nextPlayerId++;
    const name = sanitizeName(rawName, id);
    const fresh = this.authorityId === 0;
    const member: Member = { id, name, peer, ready: false };
    this.members.set(id, member);

    if (fresh && this.initial) {
      // First player of a room opened from a save: they load it, and become the authority.
      peer.send({
        type: 'welcome',
        playerId: id,
        roomCode: this.code,
        start: { kind: 'snapshot' },
      });
      peer.send({ type: 'loadSnapshot', data: this.initial });
      this.initial = null;
    } else if (fresh) {
      // First player: everyone (just them) starts the same brand-new world from the room seed.
      peer.send({
        type: 'welcome',
        playerId: id,
        roomCode: this.code,
        start: { kind: 'new', seed: this.seed },
      });
    } else {
      peer.send({
        type: 'welcome',
        playerId: id,
        roomCode: this.code,
        start: { kind: 'snapshot' },
      });
      this.startSync([id], now);
    }
    this.broadcastState();
    return id;
  }

  leave(id: number, now: number): void {
    const wasAuthority = id === this.authorityId;
    if (!this.members.delete(id)) return;
    if (this.sync) {
      this.sync.targets.delete(id);
      if (this.sync.targets.size === 0) {
        this.sync = null;
      } else if (wasAuthority) {
        // The snapshot source left mid-sync: ask the new authority instead.
        this.startSync([...this.sync.targets], now);
      }
    }
    this.broadcastState();
  }

  receive(id: number, msg: ClientMessage, now: number): void {
    const member = this.members.get(id);
    if (!member) return;
    switch (msg.type) {
      case 'command':
        if (member.ready) this.pending.push({ playerId: id, command: msg.command });
        break;
      case 'ready':
        this.onReady(member);
        break;
      case 'snapshot':
        if (this.sync && id === this.authorityId && msg.seq === this.sync.seq) {
          for (const targetId of this.sync.targets) {
            this.members.get(targetId)?.peer.send({ type: 'loadSnapshot', data: msg.data });
          }
        }
        break;
      case 'hash':
        if (member.ready) this.onHash(id, msg.seq, msg.hash, now);
        break;
      case 'setSpeed':
        if ((SPEEDS as readonly number[]).includes(msg.speed) && msg.speed !== this.speed) {
          this.speed = msg.speed;
          this.broadcastState();
        }
        break;
      case 'ping':
        member.peer.send({ type: 'pong', time: msg.time });
        break;
      case 'chat': {
        // By code point, so an emoji is never cut in half.
        const text = Array.from(msg.text.trim()).slice(0, MAX_CHAT_LENGTH).join('');
        if (text) this.broadcast({ type: 'chat', playerId: id, text });
        break;
      }
      case 'mark':
        // Tiles on any map we make (the largest is 384 wide).
        if (msg.x >= 0 && msg.z >= 0 && msg.x < 4096 && msg.z < 4096) {
          this.broadcast({ type: 'mark', playerId: id, x: msg.x, z: msg.z });
        }
        break;
      case 'hello':
        break; // Handled by the host before the player is in a room.
    }
  }

  update(now: number): void {
    const last = this.lastUpdate ?? now;
    this.lastUpdate = now;

    if (this.sync) {
      if (now > this.sync.deadline) this.abortSync();
      return; // Frozen: no bundles while someone loads a snapshot.
    }
    if (this.authorityId === 0) return;

    if (this.speed === 0) {
      this.owedTicks = 0;
      // Paused, but building still works: commands go out without advancing time.
      if (this.pending.length > 0) this.issue(false);
      return;
    }

    this.owedTicks += ((now - last) * TICKS_PER_SECOND * this.speed) / 1000;
    let count = Math.floor(this.owedTicks);
    if (count > MAX_BUNDLES_PER_UPDATE) {
      count = MAX_BUNDLES_PER_UPDATE;
      this.owedTicks = 0; // Host stalled: drop the backlog instead of fast-forwarding.
    } else {
      this.owedTicks -= count;
    }
    for (let i = 0; i < count; i++) this.issue(true);
  }

  private issue(advance: boolean): void {
    const bundle = { seq: this.nextSeq++, advance, commands: this.pending };
    this.pending = [];
    for (const m of this.members.values()) {
      if (m.ready) m.peer.send({ type: 'bundle', bundle });
    }
  }

  private onReady(member: Member): void {
    if (member.ready) return;
    member.ready = true;
    if (this.sync) {
      this.sync.targets.delete(member.id);
      if (this.sync.targets.size === 0) this.sync = null;
    }
    this.broadcastState();
  }

  /** Freezes the room and asks the authority for a snapshot to send to `targets`. */
  private startSync(targets: number[], now: number): void {
    const authority = this.members.get(this.authorityId);
    if (!authority) return;
    this.sync = { seq: this.nextSeq, targets: new Set(targets), deadline: now + SYNC_TIMEOUT_MS };
    authority.peer.send({ type: 'requestSnapshot', seq: this.nextSeq });
    this.broadcastState();
  }

  private abortSync(): void {
    const sync = this.sync;
    this.sync = null;
    if (!sync) return;
    for (const id of sync.targets) {
      const m = this.members.get(id);
      if (!m) continue;
      m.peer.send({ type: 'error', code: 'syncTimeout', message: 'Timed out loading the world' });
      this.members.delete(id);
      m.peer.close();
    }
    this.broadcastState();
  }

  private onHash(id: number, seq: number, hash: number, now: number): void {
    if (seq % HASH_INTERVAL !== 0) return;
    let round = this.hashes.get(seq);
    if (!round) {
      round = new Map();
      this.hashes.set(seq, round);
      const oldest = seq - HASH_HISTORY * HASH_INTERVAL;
      for (const s of this.hashes.keys()) if (s < oldest) this.hashes.delete(s);
    }
    round.set(id, hash);

    const authorityId = this.authorityId;
    const reference = round.get(authorityId);
    if (reference === undefined) return;

    const diverged: number[] = [];
    for (const [pid, h] of round) {
      if (pid === authorityId) continue;
      if (h !== reference) diverged.push(pid);
      round.delete(pid);
    }
    if (diverged.length === 0 || this.sync) return;

    for (const pid of diverged) {
      const m = this.members.get(pid);
      if (m) m.ready = false;
      this.broadcast({ type: 'desync', seq, playerId: pid });
    }
    this.startSync(diverged, now);
  }

  private broadcastState(): void {
    const players: PlayerInfo[] = [...this.members.values()].map((m) => ({
      id: m.id,
      name: m.name,
      ready: m.ready,
    }));
    this.broadcast({
      type: 'roomState',
      players,
      authorityId: this.authorityId,
      speed: this.speed,
      syncing: this.sync !== null,
    });
  }

  private broadcast(msg: ServerMessage): void {
    for (const m of this.members.values()) m.peer.send(msg);
  }
}

function sanitizeName(raw: string, id: number): string {
  const printable = [...raw].filter((ch) => (ch.codePointAt(0) ?? 0) >= 0x20).join('');
  const name = printable.trim().slice(0, MAX_NAME_LENGTH);
  return name || `Player ${id}`;
}
